import { Helmet } from "react-helmet-async";
import { brand, founder } from "@/data/site/brand";

/**
 * Sitewide Organization + Founder (Person) schema. Strengthens E-E-A-T
 * signals for this YMYL (finance) site by exposing reviewer credentials,
 * professional memberships, and a stable @id for cross-referencing.
 */
export const LocalBusinessSchema = () => {
  const orgId = `${brand.siteUrl}/#organization`;
  const personId = `${brand.siteUrl}/about/franklin-sofi#person`;

  const person = {
    "@type": "Person",
    "@id": personId,
    name: founder.name,
    jobTitle: founder.title,
    description: founder.bio,
    url: `${brand.siteUrl}/about/franklin-sofi`,
    worksFor: { "@id": orgId },
    knowsAbout: [
      "IRS tax resolution",
      "Offer in Compromise",
      "IRS audit defense",
      "Wage garnishment release",
      "Payroll tax (Form 941) liabilities",
      "Innocent spouse relief",
      "Currently Not Collectible status",
      "Federal tax liens",
    ],
    hasCredential: founder.credentialsList.map((c) => ({
      "@type": "EducationalOccupationalCredential",
      credentialCategory: c,
    })),
    memberOf: [
      { "@type": "Organization", name: "American Institute of CPAs (AICPA)" },
      { "@type": "Organization", name: "Texas Society of CPAs (TXCPA)" },
      { "@type": "Organization", name: "American Society of Tax Problem Solvers (ASTPS)" },
    ],
  };

  const organization = {
    "@type": ["AccountingService", "ProfessionalService", "Organization"],
    "@id": orgId,
    name: brand.legalName,
    alternateName: brand.shortName,
    description: brand.description,
    url: brand.siteUrl,
    telephone: brand.phone,
    email: brand.email,
    priceRange: "$$",
    foundingDate: String(brand.founded),
    logo: { "@type": "ImageObject", url: `${brand.siteUrl}/og-default.jpg` },
    image: `${brand.siteUrl}/og-default.jpg`,
    address: {
      "@type": "PostalAddress",
      streetAddress: brand.address.street,
      addressLocality: brand.address.city,
      addressRegion: brand.address.region,
      postalCode: brand.address.postalCode,
      addressCountry: brand.address.country,
    },
    areaServed: { "@type": "Country", name: "United States" },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: brand.hoursOpen,
        closes: brand.hoursClose,
      },
    ],
    founder: { "@id": personId },
    employee: [{ "@id": personId }],
    knowsAbout: [
      "IRS tax resolution",
      "Tax debt settlement",
      "IRS audit representation",
      "Wage garnishment release",
      "Payroll tax problems",
    ],
    sameAs: [brand.social.google, brand.social.facebook, brand.social.linkedin],
  };

  const website = {
    "@type": "WebSite",
    "@id": `${brand.siteUrl}/#website`,
    url: brand.siteUrl,
    name: brand.shortName,
    alternateName: brand.legalName,
    description: brand.description,
    publisher: { "@id": orgId },
    inLanguage: "en-US",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${brand.siteUrl}/blog?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };


  const graph = {
    "@context": "https://schema.org",
    "@graph": [organization, person, website],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(graph)}</script>
    </Helmet>
  );
};
